import React, { useEffect, useState } from 'react';

import './IncentiveForm.scss';
import Input from '../Input/Input';
import { Constants, ShowTypes } from '@eagerdog/constants';
import Dropdown, { IOption } from '../Dropdown/Dropdown';
import { apiService } from 'src/services';
import { toast } from '../Toast/ToastManager';
import Button from '../Button/Button';
import DatePick from "../DatePick/DatePick";
import {
	IEventSummary, IIncentive,
	IIncentiveParamsAllOfferedClasses,
	IIncentiveParamsDogsPerShow,
	IIncentiveParamsNumberOfEntries,
	IIncentiveParamsNumberOfEntriesFeePercentage, IIncentiveParamsNumberOfShows
} from "@eagerdog/interfaces";
import IncentiveDynamicList from "./components/IncentiveDynamicList";

interface IncentiveFormProps {
	loaded: boolean,
  onFinish: () => void,
  onCancel?: () => void,
  event: IEventSummary,
	incentive: IIncentive | null
}

const IncentiveForm: React.FC<IncentiveFormProps> = (props: IncentiveFormProps) => {

	const incentiveTypes:IOption[] = React.useMemo(() => {
		const entryPerTrialLabel = props?.event?.sanctioning_club === "AKC"
			? "Number of Entries per AKC Event Number"
			: "Number of Entries per Trial";
		return [
			{ value: "Number of Show Types", id: Constants.incentive_type.min_number_of_show_types },
			{ value: "Number of Entries (%)", id: Constants.incentive_type.number_of_entries_fee_percentage },
			{ value: entryPerTrialLabel, id: Constants.incentive_type.number_of_entries_per_event_number },
			{ value: "Number of Entries per Show", id: Constants.incentive_type.number_of_entries_per_show },
			{ value: "Number of Entries per Day", id: Constants.incentive_type.number_of_entries_per_day },
			{ value: "Number of Entries per Event", id: Constants.incentive_type.number_of_entries_per_event },
			{ value: "Dogs per Show", id: Constants.incentive_type.dogs_per_show },
			{ value: "All Offered Classes", id: Constants.incentive_type.all_offered_classes },
			{ value: "Number of Shows", id: Constants.incentive_type.number_of_shows },
		{ value: "Per Entry Fee Change (Per Dog, Per Event)", id: Constants.incentive_type.number_of_entries_per_event_per_dog },
		]
	}, [props?.event?.sanctioning_club]);

	const [incentiveType, setIncentiveType] = React.useState<IOption>(
		props.incentive?.incentive_type ?
			incentiveTypes.find((i) => i.id === props.incentive?.incentive_type) ?? incentiveTypes[0]
			:
			incentiveTypes[0]
	);

	const [numberOfShowTypes, setNumberOfShowTypes] = React.useState<number>(1);
	const [fee, setFee] = React.useState<number>(1);
	const [feeInput, setFeeInput] = React.useState<string>("1");
	const [feePercentage, setFeePercentage] = React.useState<IIncentiveParamsNumberOfEntriesFeePercentage[]>([]);
	const [feePercentageInputs, setFeePercentageInputs] = React.useState<string[]>([]);

	const [fieldNumberOfEntries, setFieldNumberOfEntries] = React.useState<IIncentiveParamsNumberOfEntries[]>([]);
	const [fieldDogsPerShow, setFieldDogsPerShow] = React.useState<IIncentiveParamsDogsPerShow[]>([]);
	const [fieldAllOfferedClasses, setFieldAllOfferedClasses] = React.useState<IIncentiveParamsAllOfferedClasses[]>([]);

	const [fieldNumberOfShows, setFieldNumberOfShows] = React.useState<IIncentiveParamsNumberOfShows[]>(
		incentiveType.id === Constants.incentive_type.number_of_shows ? props.incentive?.number_of_shows ?? [] : []
	);

	const [expiryDate, setExpiryDate] = useState<undefined | Date>(
		props.incentive?.expires_at ? new Date(props.incentive.expires_at) : undefined
	);

	useEffect(() => {
		if (props.loaded && props.incentive) {
			if (!expiryDate && props.incentive.expires_at) {
				setExpiryDate(new Date(props.incentive.expires_at));
			}
			if (incentiveType) {
				setIncentiveType(incentiveType)
				switch (incentiveType.id) {
					case Constants.incentive_type.min_number_of_show_types:
						setNumberOfShowTypes(props.incentive?.min_number_of_show_types?.number_of_show_types ?? 1)
						const nextFee = props.incentive?.min_number_of_show_types?.fee ?? 1
						setFee(nextFee)
						setFeeInput(nextFee.toString())
						break
					case Constants.incentive_type.number_of_entries_fee_percentage:
						const nextFeePercentage = props.incentive?.number_of_entries_fee_percentage ?? []
						setFeePercentage(nextFeePercentage)
						setFeePercentageInputs(nextFeePercentage.map((entry) => entry.fee_percentage?.toString() ?? ""))
						break
					case Constants.incentive_type.number_of_entries_per_event_number:
						setFieldNumberOfEntries(props.incentive?.number_of_entries_per_event_number ?? [])
						break
					case Constants.incentive_type.number_of_entries_per_day:
						setFieldNumberOfEntries(props.incentive?.number_of_entries_per_day ?? [])
						break
					case Constants.incentive_type.number_of_entries_per_show:
						setFieldNumberOfEntries(props.incentive?.number_of_entries_per_show ?? [])
						break
					case Constants.incentive_type.number_of_entries_per_event:
						setFieldNumberOfEntries(props.incentive?.number_of_entries_per_event ?? [])
						break
					case Constants.incentive_type.dogs_per_show:
						setFieldDogsPerShow(props.incentive?.dogs_per_show ?? [])
						break
					case Constants.incentive_type.all_offered_classes:
						setFieldAllOfferedClasses(props.incentive?.all_offered_classes ?? [])
						break
				case Constants.incentive_type.number_of_shows:
					setFieldNumberOfShows(props.incentive?.number_of_shows ?? [])
					break
				case Constants.incentive_type.number_of_entries_per_event_per_dog:
					setFieldNumberOfEntries(props.incentive?.number_of_entries_per_event_per_dog ?? [])
					break
				default:
						break
				}
			}
		}
	}, [props.incentive, props.loaded, incentiveType, incentiveTypes, expiryDate]);

	const club = props.event.sanctioning_club;

	const availableShowTypes = React.useMemo(() => {
		return ShowTypes[club] || {};
	}, [club]);

	const showElementsWithLevels = React.useMemo(() => {
		const result: Record<string, string[]> = {};

		Object.entries(availableShowTypes).forEach(([showTypeKey, children]) => {
			if (!children || typeof children !== "object") return;

			const directLevels: string[] = [];

			Object.entries(children).forEach(([childKey, subLevels]) => {
				if (Array.isArray(subLevels) && subLevels.length > 0) {
					// Some element labels (e.g. "Non-Licensed Classes") exist under multiple show types.
					// Merge levels instead of overwriting so all valid options remain available.
					const existingLevels = result[childKey] || [];
					result[childKey] = Array.from(new Set([...existingLevels, ...subLevels]));
				} else if (
					subLevels &&
					typeof subLevels === "object" &&
					!Array.isArray(subLevels) &&
					Array.isArray((subLevels as any).levels)
				) {
					// Handle { levels: string[], sections?: string[] } shape
					// (e.g. ABI Scent Dog Games element)
					const levels = (subLevels as any).levels as string[];
					const existingLevels = result[childKey] || [];
					result[childKey] = Array.from(new Set([...existingLevels, ...levels]));
				} else {
					directLevels.push(childKey);
				}
			});

			if (directLevels.length > 0) {
				result[showTypeKey] = directLevels;
			}
		});

		return result;
	}, [availableShowTypes]);

	const handleIncentive = () => {
		let payload: any = {
			incentive_type: incentiveType.id,
			expires_at: expiryDate ? expiryDate.toISOString() : undefined,
			club_id: props.event.club_id.toString(),
			event_id: props.event._id.toString()
		}

		switch (incentiveType.id) {
			case Constants.incentive_type.min_number_of_show_types:
				payload.min_number_of_show_types = {
					number_of_show_types: numberOfShowTypes,
					fee,
				};
				break;

			case Constants.incentive_type.number_of_entries_fee_percentage:
				payload.number_of_entries_fee_percentage = feePercentage;
				break;

			case Constants.incentive_type.number_of_entries_per_event_number:
				payload.number_of_entries_per_event_number = fieldNumberOfEntries;
				break;

			case Constants.incentive_type.number_of_entries_per_day:
				payload.number_of_entries_per_day = fieldNumberOfEntries;
				break;

			case Constants.incentive_type.number_of_entries_per_show:
				payload.number_of_entries_per_show = fieldNumberOfEntries;
				break;

			case Constants.incentive_type.number_of_entries_per_event:
				payload.number_of_entries_per_event = fieldNumberOfEntries;
				break;

		case Constants.incentive_type.dogs_per_show:
			payload.dogs_per_show = fieldDogsPerShow;
			break;

		case Constants.incentive_type.all_offered_classes:
			payload.all_offered_classes = fieldAllOfferedClasses;
			break;

		case Constants.incentive_type.number_of_shows:
			payload.number_of_shows = fieldNumberOfShows;
			break;

		case Constants.incentive_type.number_of_entries_per_event_per_dog:
			payload.number_of_entries_per_event_per_dog = fieldNumberOfEntries;
			break;
		}

	if (props.incentive) {
		apiService.updateIncentive(props.event._id.toString(), props.incentive._id.toString(), payload)
			.then((res) => {
				toast.show({
					title: "Incentive Updated",
					content: `Successfully updated incentive.`,
					duration: 10000,
					type: "success"
				});

				props.onFinish();
			})
			.catch((error) => {
				toast.show({
					title: "Error Updating Incentive",
					content: error?.response?.data?.message || "Unable to update incentive. Please try again.",
					duration: 10000,
					type: "fail"
				});
			});
	} else {
		apiService.createIncentive(props.event._id.toString(), payload)
			.then((res) => {
				toast.show({
					title: "Incentive Created",
					content: `Successfully created incentive.`,
					duration: 10000,
					type: "success"
				});

				props.onFinish();
			})
			.catch((error) => {
				toast.show({
					title: "Error Creating Incentive",
					content: error?.response?.data?.message || "Unable to create incentive. Please try again.",
					duration: 10000,
					type: "fail"
				});
			});
	}
	}

	const handleDeleteIncentive = () => {
		if (!props.incentive) return;
		if (!window.confirm("Delete this incentive? New entries will be charged the regular class fees.")) return;

		apiService.deleteIncentive(props.event._id.toString(), props.incentive._id.toString())
			.then(() => {
				toast.show({
					title: "Incentive Deleted",
					content: "Successfully deleted incentive.",
					duration: 10000,
					type: "success"
				});

				props.onFinish();
			})
			.catch((error) => {
				toast.show({
					title: "Error Deleting Incentive",
					content: error?.response?.data?.message || "Unable to delete incentive. Please try again.",
					duration: 10000,
					type: "fail"
				});
			});
	}

	return (
		<form className='IncentiveForm' onSubmit={(e) => { e.preventDefault(); handleIncentive(); }}>
			{props.loaded && (
				<>
					<div className="formWrap">
						<Dropdown
							value={incentiveType}
							onChange={(_e, value: IOption) => {
								if (incentiveType.id !== value.id) {
									setIncentiveType(value);
								}
							}}
							label="Incentive Type"
							options={incentiveTypes}
							placeholder="Choose incentive type."
						/>
					<DatePick
						isClearable
						showTimeSelect
						label="Expiry Date"
						placeholder="(optional)"
						value={expiryDate}
						onChange={(date: Date | null) => {
							if (date) {
								const utcDate = new Date(Date.UTC(
									date.getUTCFullYear(),
									date.getUTCMonth(),
									date.getUTCDate(),
									date.getUTCHours(),
									date.getUTCMinutes(),
									0, 0
								));
								setExpiryDate(utcDate);
							} else {
								setExpiryDate(undefined);
							}
						}}
					/>
						{incentiveType.id === Constants.incentive_type.min_number_of_show_types && (
							<>
								<Input
									value={numberOfShowTypes}
									label="Number of show types"
									type="number"
									step="1"
									onChange={(e) => { e.stopPropagation(); setNumberOfShowTypes(parseFloat(e.target.value)) }}
									placeholder="Set Count of Different Shows"
								/>
								<Input
									value={feeInput}
									label="Fee"
									type="number"
									step="any"
									onChange={(e) => {
										e.stopPropagation();
										const nextValue = e.target.value;
										setFeeInput(nextValue);
										const parsed = parseFloat(nextValue);
										if (!Number.isNaN(parsed)) {
											setFee(parsed);
										}
									}}
									onBlur={() => {
										if (feeInput.trim().length === 0) {
											setFeeInput("");
											return;
										}
										const parsed = parseFloat(feeInput);
										if (!Number.isNaN(parsed)) {
											setFee(parsed);
											setFeeInput(parsed.toString());
										}
									}}
									placeholder="Set Fee"
								/>
							</>
						)}
						{incentiveType.id === Constants.incentive_type.number_of_entries_fee_percentage && (
							<>
								{feePercentage.map((element, index) => (
									<div key={index} className={"feePercentageRow"}>
										<Input
											value={element.number_of_entries}
											label="Number of Entries"
											type="number"
											step="1"
											placeholder="Set Number of entries"
											onChange={(e) => {
												e.stopPropagation();
												const updated = [...feePercentage];
												updated[index].number_of_entries = parseFloat(e.target.value);
												setFeePercentage(updated);
											}}
										/>
										<Input
											value={feePercentageInputs[index] ?? ""}
											label="Fee Percentage"
											type="number"
											step="any"
											placeholder="Set Fee Percentage"
											onChange={(e) => {
												e.stopPropagation();
												const nextValue = e.target.value;
												const nextInputs = [...feePercentageInputs];
												nextInputs[index] = nextValue;
												setFeePercentageInputs(nextInputs);
												const parsed = parseFloat(nextValue);
												if (!Number.isNaN(parsed)) {
													const updated = [...feePercentage];
													updated[index].fee_percentage = parsed;
													setFeePercentage(updated);
												}
											}}
											onBlur={() => {
												const raw = feePercentageInputs[index] ?? "";
												if (raw.trim().length === 0) {
													const nextInputs = [...feePercentageInputs];
													nextInputs[index] = "";
													setFeePercentageInputs(nextInputs);
													return;
												}
												const parsed = parseFloat(raw);
												if (!Number.isNaN(parsed)) {
													const updated = [...feePercentage];
													updated[index].fee_percentage = parsed;
													setFeePercentage(updated);
													const nextInputs = [...feePercentageInputs];
													nextInputs[index] = parsed.toString();
													setFeePercentageInputs(nextInputs);
												}
											}}
										/>
									</div>
								))}
								<Button
									type="button"
									onClick={() => {
										setFeePercentage([
											...feePercentage,
											{ number_of_entries: 0, fee_percentage: 0 },
										]);
										setFeePercentageInputs([
											...feePercentageInputs,
											"0",
										]);
									}}
								>
									+ Add Fee Percentage
								</Button>
							</>
						)}
						{[
							Constants.incentive_type.number_of_entries_per_event_number,
							Constants.incentive_type.number_of_entries_per_day,
							Constants.incentive_type.number_of_entries_per_show,
							Constants.incentive_type.number_of_entries_per_event,
							Constants.incentive_type.number_of_entries_per_event_per_dog,
						].includes(incentiveType.id) && (
							<IncentiveDynamicList
								type="entries"
								items={fieldNumberOfEntries}
								setItems={setFieldNumberOfEntries}
								showElementsWithLevels={showElementsWithLevels}
								availableShowTypes={availableShowTypes}
							enableEntryClassOverrideConditions={
								incentiveType.id === Constants.incentive_type.number_of_entries_per_event_number ||
								incentiveType.id === Constants.incentive_type.number_of_entries_per_show
							}
							/>
						)}
						{incentiveType.id === Constants.incentive_type.dogs_per_show && (
							<IncentiveDynamicList
								type="dogs"
								items={fieldDogsPerShow}
								setItems={setFieldDogsPerShow}
								showElementsWithLevels={showElementsWithLevels}
								availableShowTypes={availableShowTypes}
							/>
						)}
						{incentiveType.id === Constants.incentive_type.all_offered_classes && (
							<IncentiveDynamicList
								type="all_offered_classes"
								items={fieldAllOfferedClasses}
								setItems={setFieldAllOfferedClasses}
								showElementsWithLevels={showElementsWithLevels}
								availableShowTypes={availableShowTypes}
							/>
						)}
						{incentiveType.id === Constants.incentive_type.number_of_shows && (
							<IncentiveDynamicList
								type="shows"
								items={fieldNumberOfShows}
								setItems={setFieldNumberOfShows}
								showElementsWithLevels={showElementsWithLevels}
								availableShowTypes={availableShowTypes}
							/>
						)}
					</div>
					<div className="actions">
						{props.incentive && (
							<Button type="button" variant="secondary" className="deleteIncentive" onClick={handleDeleteIncentive}>Delete</Button>
						)}
						{!props.incentive && props.onCancel && (
							<Button type="button" variant="secondary" onClick={props.onCancel}>Cancel</Button>
						)}
						<Button type="submit">{props.incentive ? "Save" : "Create Incentive"}</Button>
					</div>
				</>
			)}
		</form>
	)
}

export default IncentiveForm;
